import { type FormEvent, useMemo, useState } from "react";
import { useAuth } from "@/auth/AuthProvider";
import { useToast } from "@/components/Toast";
import { type BlogPost, type BlogPostInput, uploadCoverImage, useSavePost } from "./api";
import { CATEGORIES, COVER_KEYS, COVER_STYLES, slugify } from "./format";
import { renderMarkdown } from "./markdown";

const ACCEPTED = ["image/png", "image/jpeg", "image/webp", "image/gif"];
const MAX_BYTES = 5 * 1024 * 1024;

type Draft = BlogPostInput & { slug: string };

function draftFrom(post: BlogPost | null): Draft {
  return {
    title: post?.title ?? "",
    slug: post?.slug ?? "",
    authorName: post?.authorName ?? "",
    category: post?.category || CATEGORIES[0],
    excerpt: post?.excerpt ?? "",
    coverStyle: post?.coverStyle || COVER_KEYS[0],
    coverImageUrl: post?.coverImageUrl ?? "",
    contentMd: post?.contentMd ?? "",
    published: post ? Boolean(post.published) : true,
  };
}

/** The authoring dialog: create a post or edit one (legacy BlogComposer). */
export function BlogComposer({
  initialPost,
  onClose,
}: {
  initialPost: BlogPost | null;
  onClose: () => void;
}) {
  const isEdit = Boolean(initialPost);
  const toast = useToast();
  const { user } = useAuth();
  const save = useSavePost();
  const [form, setForm] = useState<Draft>(() => draftFrom(initialPost));
  const [slugTouched, setSlugTouched] = useState(isEdit);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const preview = useMemo(() => renderMarkdown(form.contentMd), [form.contentMd]);

  const set = <K extends keyof Draft>(key: K, value: Draft[K]) =>
    setForm((prev) => {
      const next = { ...prev, [key]: value };
      if (key === "title" && !slugTouched && !isEdit) next.slug = slugify(String(value));
      return next;
    });

  const onCover = async (file: File | undefined) => {
    if (!file) return;
    if (!ACCEPTED.includes(file.type))
      return toast("Please choose a PNG, JPEG, WebP or GIF image.");
    if (file.size > MAX_BYTES) return toast("That image is too large (max 5 MB).");
    setUploading(true);
    try {
      const token = user ? await user.getIdToken() : null;
      const url = await uploadCoverImage(file, token);
      set("coverImageUrl", url);
      toast("Cover image uploaded.");
    } catch (e) {
      toast(e instanceof Error ? e.message : "Could not upload the image.");
    } finally {
      setUploading(false);
    }
  };

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!form.title.trim()) return setError("A title is required.");
    if (!form.contentMd.trim()) return setError("The post body cannot be empty.");
    setError(null);
    const { slug, ...rest } = form;
    const post: BlogPostInput = { ...rest, authorName: form.authorName.trim() };
    save.mutate(
      isEdit
        ? { slug: initialPost!.slug, post }
        : { post: { ...post, slug: slug || slugify(form.title) } },
      {
        onSuccess: () => {
          toast(
            isEdit
              ? form.published
                ? "Post updated."
                : "Saved as draft."
              : form.published
                ? "Post published."
                : "Draft saved.",
          );
          onClose();
        },
        onError: (err) => setError(err instanceof Error ? err.message : "Could not save the post."),
      },
    );
  };

  const fileInput = (
    <input
      type="file"
      accept={ACCEPTED.join(",")}
      hidden
      disabled={uploading}
      onChange={(e) => {
        const f = e.target.files?.[0];
        e.target.value = "";
        void onCover(f);
      }}
    />
  );

  return (
    <div
      className="sadmin-overlay"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <form
        className="sadmin-dialog"
        onSubmit={submit}
        role="dialog"
        aria-modal="true"
        aria-labelledby="composer-title"
      >
        <div className="sadmin-dhead">
          <div>
            <div className="sadmin-eyebrow">Admin · Authoring</div>
            <h2 id="composer-title">{isEdit ? "Edit post" : "New post"}</h2>
          </div>
          <button type="button" className="wp-btn g" aria-label="Close" onClick={onClose}>
            ✕
          </button>
        </div>

        <div className="sadmin-dbody">
          <div className="sadmin-two">
            <label className="wp-field">
              Title
              <input value={form.title} onChange={(e) => set("title", e.target.value)} />
            </label>
            <label className="wp-field">
              Slug
              <input
                value={form.slug}
                disabled={isEdit}
                onChange={(e) => {
                  setSlugTouched(true);
                  set("slug", slugify(e.target.value));
                }}
              />
            </label>
          </div>
          <div className="sadmin-two">
            <label className="wp-field">
              Author
              <input
                value={form.authorName}
                placeholder="e.g. Jane Doe"
                onChange={(e) => set("authorName", e.target.value)}
              />
            </label>
            <label className="wp-field">
              Category
              <select value={form.category} onChange={(e) => set("category", e.target.value)}>
                {CATEGORIES.map((c) => (
                  <option key={c}>{c}</option>
                ))}
              </select>
            </label>
          </div>
          <label className="wp-field">
            Excerpt
            <input value={form.excerpt} onChange={(e) => set("excerpt", e.target.value)} />
          </label>

          <div className="wp-field">
            <span>Cover image</span>
            {form.coverImageUrl ? (
              <div className="sadmin-cover">
                <img src={form.coverImageUrl} alt="Cover preview" />
                <label className="wp-btn g">
                  {uploading ? "Uploading…" : "Replace"}
                  {fileInput}
                </label>
                <button
                  type="button"
                  className="wp-btn g danger"
                  onClick={() => set("coverImageUrl", "")}
                >
                  Remove
                </button>
              </div>
            ) : (
              <label className="sadmin-drop">
                <b>{uploading ? "Uploading…" : "Upload a cover image"}</b>
                <span>PNG, JPEG, WebP or GIF · up to 5 MB</span>
                {fileInput}
              </label>
            )}
            <span className="sadmin-hint">
              {form.coverImageUrl
                ? "Gradient fallback (used if the image is removed)"
                : "…or pick a gradient"}
            </span>
            <div className="sadmin-swatches">
              {COVER_KEYS.map((key) => (
                <button
                  type="button"
                  key={key}
                  aria-label={`Cover gradient ${key}`}
                  aria-pressed={form.coverStyle === key}
                  style={{ background: COVER_STYLES[key] }}
                  onClick={() => set("coverStyle", key)}
                />
              ))}
            </div>
          </div>

          <div className="wp-field">
            <span>Body · Markdown</span>
            <div className="sadmin-two">
              <textarea
                aria-label="Body (Markdown)"
                value={form.contentMd}
                onChange={(e) => set("contentMd", e.target.value)}
                placeholder={
                  "## A heading\n\nWrite in **Markdown**. Lists, > quotes, `code`, and [links](https://…) all work."
                }
              />
              <div className="sadmin-preview">
                <div className="sadmin-eyebrow">Live preview</div>
                <div className="sadmin-prose" dangerouslySetInnerHTML={{ __html: preview }} />
              </div>
            </div>
          </div>
          {error && (
            <p className="wp-form-err" role="alert">
              {error}
            </p>
          )}
        </div>

        <div className="sadmin-dfoot">
          <label className="sadmin-check">
            <input
              type="checkbox"
              checked={form.published}
              onChange={(e) => set("published", e.target.checked)}
            />
            Published
          </label>
          <div className="sadmin-actions">
            <button type="button" className="wp-btn g" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="wp-btn" disabled={save.isPending}>
              {save.isPending ? "Saving…" : isEdit ? "Save changes" : "Publish post"}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}
